import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import InviteCode from './components/InviteCode';
import Dashboard from './components/Dashboard';
import LoadingSpinner from './components/LoadingSpinner';
import OfflineIndicator from './components/OfflineIndicator';
import api from './api';
import { loadTheme, loadLogo, DEFAULT_FONT } from './utils/themeStorage';
import { loadUserThemeConfig, loadUserLogoConfig } from './utils/userDataLoader';
import { applyTheme } from './utils/themeUtils';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('/UserData/Logo.svg');

  useEffect(() => {
    const loadInitialConfig = async () => {
      // Load theme and logo early so LoadingSpinner uses correct colors and logo
      
      // Load theme
      const hasThemeInStorage = localStorage.getItem('wedding_theme_config') !== null;
      if (hasThemeInStorage) {
        const theme = loadTheme();
        applyTheme(theme);
      } else {
        const themeConfigData = await loadUserThemeConfig();
        const theme = {
          colors: themeConfigData.colors || themeConfigData,
          font: themeConfigData.font || DEFAULT_FONT
        };
        applyTheme(theme);
      }

      // Load logo and wedding text
      const hasLogoInStorage = localStorage.getItem('wedding_logo_config') !== null;
      let weddingText = 'Wedding Gallery'; // Default title
      if (hasLogoInStorage) {
        const { logo, text } = loadLogo();
        setLogoUrl(logo);
        if (text) {
          weddingText = text;
        }
      } else {
        const logoConfig = await loadUserLogoConfig();
        setLogoUrl(logoConfig.logo);
        if (logoConfig.text) {
          weddingText = logoConfig.text;
        }
      }
      // Update document title
      document.title = weddingText;
    };

    const validateToken = async () => {
      const token = localStorage.getItem('weddingToken');
      if (!token) {
        // No token - warm up API for new users who will enter invite code
        // This ensures API is warm by the time they submit the form
        api.get('/health').catch(() => {
          // Silently fail - this is just a warm-up ping
          // If it fails, the auth request will wake it up anyway
        });
        setIsLoading(false);
        return;
      }

      // Token exists - validate it (this also warms up the API)
      // No need for separate health check since this request will warm it up
      try {
        // Test the token by making a request to the photos endpoint
        await api.get('/photos', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Token validation failed:', error);
        // Token is invalid or expired, remove it and redirect to login
        localStorage.removeItem('weddingToken');
        setIsAuthenticated(false);
      }
      setIsLoading(false);
    };

    // Run initialization sequentially to avoid race conditions
    const init = async () => {
      await loadInitialConfig();  // Load config first (sets logo)
      await validateToken();      // Then validate token (sets isLoading)
    };
    init();

    // Listen for auth errors from API interceptor
    const handleAuthError = (event) => {
      console.warn('App: Auth error event received', event.detail);
      setIsAuthenticated(false);
    };
    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, []);

  if (isLoading) {
    return <LoadingSpinner logo={logoUrl} />;
  }

  return (
    <>
      <OfflineIndicator />
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Routes>
          <Route path="/" element={isAuthenticated ? <Navigate to="/dashboard" /> : <InviteCode setAuth={setIsAuthenticated} logo={logoUrl} />} />
          <Route path="/dashboard/*" element={isAuthenticated ? <Dashboard logo={logoUrl} /> : <Navigate to="/" />} />
        </Routes>
      </Router>
    </>
  );
}

export default App;